import { useQuery } from '@tanstack/react-query'
import { MessageSquare, Hash, Users, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'

interface ChannelSummary {
  id: string
  name: string
  scope: string
  artistId: string
  muted: boolean
  archived: boolean
  messageCount: number
  unreadCount: number
  latestMessage: { body: string; authorType: string; createdAt: string } | null
}

export function CrewMessagesPage() {
  const { data, isLoading } = useQuery<ChannelSummary[], ApiClientError>({
    queryKey: ['crew-channels'],
    queryFn: () => apiFetch('/crew/profile/channels'),
  })

  return (
    <div>
      <PageHeader title="Messages" subtitle="Chat channels from your artist teams" />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
        </div>
      ) : !data || data.length === 0 ? (
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          No channels yet. You'll be added automatically when an artist team creates a channel for your tour, show, or department.
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((ch) => (
            <Link
              key={ch.id}
              to={`/crew/messages/${ch.id}`}
              className="flex items-center justify-between rounded-lg border border-ovation-ink/10 bg-white p-4 transition-colors hover:border-ovation-ink/25"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ovation-ink/5">
                  <ScopeIcon scope={ch.scope} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ovation-ink truncate">{ch.name}</span>
                    {ch.muted && <Lock className="h-3 w-3 text-ovation-muted" />}
                    {ch.archived && (
                      <span className="rounded bg-ovation-ink/10 px-1.5 py-0.5 text-[10px] uppercase">
                        archived
                      </span>
                    )}
                  </div>
                  {ch.latestMessage && (
                    <p className="mt-0.5 truncate text-xs text-ovation-muted">
                      {ch.latestMessage.authorType === 'admin' ? 'Admin: ' : ''}
                      {ch.latestMessage.body.slice(0, 80)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 ml-3">
                {ch.latestMessage && (
                  <span className="text-[10px] text-ovation-muted">
                    {formatTime(ch.latestMessage.createdAt)}
                  </span>
                )}
                {ch.unreadCount > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ovation-ink px-1.5 text-[10px] font-bold text-ovation-paper">
                    {ch.unreadCount > 99 ? '99+' : ch.unreadCount}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function ScopeIcon({ scope }: { scope: string }) {
  switch (scope) {
    case 'tour': return <Users className="h-4 w-4 text-ovation-muted" />
    case 'show': return <Hash className="h-4 w-4 text-ovation-muted" />
    case 'department': return <MessageSquare className="h-4 w-4 text-ovation-muted" />
    default: return <MessageSquare className="h-4 w-4 text-ovation-muted" />
  }
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  if (diff < 60_000) return 'now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
